/**
 * ApplicantDetailsCard — applicant name + postal address capture for the
 * Payments section.
 *
 * The quote-first funnel never collects owner details before payment, but
 * IP Australia requires the applicant's name and postal address to file.
 * This card sits above the declaration and autosaves as the user types
 * (900ms debounce + flush on blur — the same pattern the old guest checkout
 * used), writing the SAME fields that wizard wrote via the applications
 * update endpoint: ownerName + ownerAddress {street, city, state, zip,
 * country} (ownerType preserved, defaulting to 'business'). No new record
 * shapes.
 *
 * Country is fixed to the quote's first country (display-only). The parent
 * receives live { name, complete } updates via onApplicantChange so the
 * quote card's Applicant row and the payment overlay gate can react.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from '../../../common';
import {
  UserRound,
  Loader2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { useUpdateApplicationMutation } from '../../../hooks/queries/applications';

const AUTOSAVE_DEBOUNCE_MS = 900;

// Same base styling as the shared Input component, with a taller touch
// target (~44px) for mobile
const FIELD_CLASSES = 'block w-full min-h-[44px] rounded-md border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6';

const Field = ({ label, value, onChange, onBlur, name, placeholder, autoComplete, required, optionalHint }) => (
  <div className="w-full">
    <label htmlFor={`applicant-${name}`} className="block text-sm font-medium mb-1 text-gray-900">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
      {optionalHint && <span className="text-gray-400 font-normal ml-1">{optionalHint}</span>}
    </label>
    <input
      id={`applicant-${name}`}
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className={FIELD_CLASSES}
    />
  </div>
);

const emptyForm = { name: '', street: '', city: '', state: '', zip: '' };

const ApplicantDetailsCard = ({ applicationId, application, fallbackName, countryName, onApplicantChange }) => {
  const { t } = useTranslation('dashboard');
  const updateApplicationMutation = useUpdateApplicationMutation();

  const [form, setForm] = useState(emptyForm);
  // 'idle' | 'saving' | 'saved' | 'error'
  const [saveState, setSaveState] = useState('idle');

  const formRef = useRef(form);
  formRef.current = form;
  const dirtyRef = useRef(false);
  const saveTimerRef = useRef(null);
  const saveSeqRef = useRef(0);
  const mutationRef = useRef(updateApplicationMutation);
  mutationRef.current = updateApplicationMutation;

  // Rehydrate from the application record (ownerName/ownerAddress written on a
  // previous visit) or fall back to the Clerk account name, which for
  // quote-flow accounts is exactly the name typed at the end of the wizard.
  // Deliberately NOT businessName: for most visitors that is just the trade
  // mark name again, which is the wrong applicant. The applications query may
  // resolve after mount, so fill any field the user has not typed in yet
  // whenever record data arrives.
  const ownerAddress = application?.ownerAddress;
  useEffect(() => {
    setForm((prev) => {
      const next = {
        name: prev.name || application?.ownerName || fallbackName || '',
        street: prev.street || ownerAddress?.street || '',
        city: prev.city || ownerAddress?.city || '',
        state: prev.state || ownerAddress?.state || '',
        zip: prev.zip || ownerAddress?.zip || '',
      };
      const changed = Object.keys(next).some((key) => next[key] !== prev[key]);
      return changed ? next : prev;
    });
  }, [application?.ownerName, fallbackName, ownerAddress]);

  // Report live to the parent (quote card Applicant row + overlay gating)
  useEffect(() => {
    if (!onApplicantChange) return;
    onApplicantChange({
      name: form.name.trim(),
      complete: Boolean(form.name.trim() && form.street.trim() && form.city.trim() && form.zip.trim()),
    });
  }, [form, onApplicantChange]);

  const persist = useCallback(async () => {
    if (!applicationId) return;
    const snapshot = formRef.current;
    const seq = ++saveSeqRef.current;
    setSaveState('saving');
    try {
      await mutationRef.current.mutateAsync({
        applicationId,
        data: {
          // Preserve an existing ownerType; the quote-first funnel defaults
          // to business applicants (same field set the old wizard wrote)
          ownerType: application?.ownerType || 'business',
          ownerName: snapshot.name.trim(),
          ownerAddress: {
            street: snapshot.street.trim(),
            city: snapshot.city.trim(),
            state: snapshot.state.trim(),
            zip: snapshot.zip.trim(),
            country: countryName || '',
          },
        },
      });
      if (seq === saveSeqRef.current) {
        dirtyRef.current = false;
        setSaveState('saved');
      }
    } catch (err) {
      console.error('Error saving applicant details:', err);
      if (seq === saveSeqRef.current) setSaveState('error');
    }
  }, [applicationId, application?.ownerType, countryName]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      persist();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [persist]);

  const handleChange = useCallback((field) => (event) => {
    const value = event.target.value;
    dirtyRef.current = true;
    setForm((prev) => ({ ...prev, [field]: value }));
    scheduleSave();
  }, [scheduleSave]);

  // Blur flushes a pending debounce immediately so nothing is lost when the
  // user tabs straight down to the declaration / payment
  const handleBlur = useCallback(() => {
    if (!dirtyRef.current) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    persist();
  }, [persist]);

  // Flush unsaved edits on unmount (fire-and-forget). Refs keep this a true
  // unmount-only cleanup regardless of persist identity changes.
  const persistRef = useRef(persist);
  persistRef.current = persist;
  useEffect(() => () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      if (dirtyRef.current) persistRef.current();
    }
  }, []);

  return (
    <Card className="border-2 border-gray-200">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <UserRound className="h-5 w-5 text-teal-700" />
          {t('payments.applicantDetails.title', 'Applicant details')}
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-normal" aria-live="polite">
            {saveState === 'saving' && (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                <span className="text-gray-500">{t('payments.applicantDetails.saving', 'Saving…')}</span>
              </>
            )}
            {saveState === 'saved' && (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-emerald-700">{t('payments.applicantDetails.saved', 'Saved')}</span>
              </>
            )}
          </span>
        </CardTitle>
        <CardDescription>
          {t('payments.applicantDetails.subtitle', 'The trade mark will be registered in this name. The trade mark office requires the applicant’s name and postal address to file.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field
          label={t('payments.applicantDetails.nameLabel', 'Applicant name')}
          name="name"
          value={form.name}
          onChange={handleChange('name')}
          onBlur={handleBlur}
          placeholder={t('payments.applicantDetails.namePlaceholder', 'Full legal name or company name')}
          autoComplete="name"
          required
        />
        <Field
          label={t('payments.applicantDetails.streetLabel', 'Street address')}
          name="street"
          value={form.street}
          onChange={handleChange('street')}
          onBlur={handleBlur}
          placeholder={t('payments.applicantDetails.streetPlaceholder', '123 Example Street')}
          autoComplete="street-address"
          required
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label={t('payments.applicantDetails.cityLabel', 'Suburb / City')}
            name="city"
            value={form.city}
            onChange={handleChange('city')}
            onBlur={handleBlur}
            autoComplete="address-level2"
            required
          />
          <Field
            label={t('payments.applicantDetails.stateLabel', 'State')}
            name="state"
            value={form.state}
            onChange={handleChange('state')}
            onBlur={handleBlur}
            autoComplete="address-level1"
            optionalHint={t('payments.applicantDetails.optional', '(optional)')}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label={t('payments.applicantDetails.zipLabel', 'Postcode')}
            name="zip"
            value={form.zip}
            onChange={handleChange('zip')}
            onBlur={handleBlur}
            autoComplete="postal-code"
            required
          />
          <div className="w-full">
            <label className="block text-sm font-medium mb-1 text-gray-900">
              {t('payments.applicantDetails.countryLabel', 'Country')}
            </label>
            <div className="flex items-center min-h-[44px] rounded-md px-3 py-2.5 bg-gray-50 ring-1 ring-inset ring-gray-200 text-gray-700 sm:text-sm">
              {countryName || '—'}
            </div>
          </div>
        </div>

        {saveState === 'error' && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{t('payments.applicantDetails.saveError', 'Could not save your details. They will be saved again as you keep typing.')}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApplicantDetailsCard;
